import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import Layout from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { IconArrowRight, IconClose, IconHeart } from '../../components/common/Icons/Icons';
import { moveToCart, shouldShowSizePicker, getInStockSizes } from '../../services/wishlistToCartService';
import SizeSelectBottomSheet from '../../components/wishlist/WishlistDrawer/SizeSelectBottomSheet';
import { BRAND } from '../../config/brand';
import './Wishlist.css';

const SITE_URL = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME = BRAND?.name || 'EVORA';

const WISHLIST_URL = `${SITE_URL}/wishlist`;
const PAGE_TITLE = `Your Saved Pieces | ${BRAND_NAME}`;
const PAGE_DESCRIPTION = 'View your saved pieces and wishlist items. Sign in to sync across devices.';
const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'Shop', item: `${SITE_URL}/shop` },
    { '@type': 'ListItem', position: 3, name: 'Wishlist', item: WISHLIST_URL },
  ],
};

function Wishlist() {
  const { wishlist, loading, removeFromWishlist, refreshWishlist } = useWishlist();
  const { isAuthenticated, token } = useAuth();
  const { openCart, updateCartFromResponse } = useCart();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [movingId, setMovingId] = useState(null);
  const [sizePickerProduct, setSizePickerProduct] = useState(null);
  const [sheetError, setSheetError] = useState(null);

  const handleMoveToCart = async (product, e, selectedSize = null) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated || !token) {
      navigate('/login?redirect=/wishlist');
      return;
    }
    const productId = product._id || product.id;
    if (!productId) return;

    // Multiple in-stock sizes and no size chosen → open size picker
    if (selectedSize == null && shouldShowSizePicker(product)) {
      setSheetError(null);
      setSizePickerProduct(product);
      return;
    }

    setMovingId(productId);
    setSheetError(null);
    try {
      const sizeToUse = selectedSize != null ? selectedSize : (product.size || 'S');
      const result = await moveToCart(String(productId), token, sizeToUse);
      if (result.success && result.cart) {
        updateCartFromResponse(result.cart);
        await refreshWishlist();
        showToast(`${product.name || 'Item'} added to your bag — you're one step closer.`, 'success');
        setSizePickerProduct(null);
        openCart();
      }
    } catch (err) {
      showToast("We couldn't add this to your bag. Please try again.", 'error');
      if (sizePickerProduct && (sizePickerProduct._id || sizePickerProduct.id) === productId) {
        setSheetError(err.message || 'Failed to move to cart');
      }
    } finally {
      setMovingId(null);
    }
  };

  const handleAddToCartWithSize = async (size) => {
    if (!sizePickerProduct) return;
    setSheetError(null);
    await handleMoveToCart(sizePickerProduct, { preventDefault: () => {}, stopPropagation: () => {} }, size);
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={WISHLIST_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={WISHLIST_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_SCHEMA)}</script>
      </Helmet>
      
      <Layout showFooter={true} showBottomTab={true}>
        <div className="wishlist-page">
          <div className="wishlist-container">
          <div className="wishlist-header">
            <h1 className="wishlist-title">Your Saved Pieces</h1>
            <p className="wishlist-subtitle">Items you've chosen to keep close.</p>
          </div>

          {loading ? (
            <div className="wishlist-loading">
              <div className="wishlist-skeleton">
                {[...Array(4)].map((_, i) => (
                  <div key={`skel-${i}`} className="wishlist-skeleton-card" aria-hidden="true" />
                ))}
              </div>
            </div>
          ) : wishlist.length > 0 ? (
            <>
              <div className="wishlist-grid">
                {wishlist.map((product) => (
                  <div key={product._id || product.id} className="wishlist-item">
                    <ProductCard product={product} />
                    <div className="wishlist-item__actions">
                      <button
                        className="wishlist-action wishlist-action--remove"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeFromWishlist(product._id || product.id);
                        }}
                        aria-label="Remove from wishlist"
                      >
                        <IconClose size={16} />
                        Remove
                      </button>
                      <button
                        className="wishlist-action wishlist-action--cart"
                        onClick={(e) => handleMoveToCart(product, e)}
                        disabled={movingId === (product._id || product.id)}
                        aria-busy={movingId === (product._id || product.id)}
                      >
                        {movingId === (product._id || product.id) ? 'Moving...' : 'Move to Cart'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {!isAuthenticated && (
                <div className="wishlist-guest-notice">
                  <p>Sign in to sync your saved pieces across devices.</p>
                  <Link to="/login" className="wishlist-guest-link">
                    Sign In <IconArrowRight size={14} />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="wishlist-empty">
              <IconHeart size={64} className="wishlist-empty__icon" />
              <h2 className="wishlist-empty__title">You haven't saved anything yet</h2>
              <p className="wishlist-empty__text">
                Start exploring and save pieces you love.
              </p>
              <div className="wishlist-empty__actions">
                <Link to="/shop" className="wishlist-empty__cta">
                  Shop Now
                </Link>
                <Link to="/men" className="wishlist-empty__link">Men's</Link>
                <span className="wishlist-empty__sep" aria-hidden="true">·</span>
                <Link to="/women" className="wishlist-empty__link">Women's</Link>
              </div>
            </div>
          )}
          </div>
        </div>
      </Layout>

      <SizeSelectBottomSheet
        isOpen={!!sizePickerProduct}
        onClose={() => { setSizePickerProduct(null); setSheetError(null); }}
        product={sizePickerProduct || null}
        inStockSizes={sizePickerProduct ? getInStockSizes(sizePickerProduct) : []}
        onAddToCart={handleAddToCartWithSize}
        loading={!!movingId}
        error={sheetError}
      />
    </>
  );
}

export default Wishlist;
